import React, { useEffect, useMemo, useRef, useState } from 'react';
import reservationsSvc from '../services/reservations';
import Icon from './Icon';
import '../style/css/reservationdrawer2026.css';

/*
 * ReservationDrawer (HOP-031) — a right-side slide-in drawer (RTL) to edit ONE reservation's
 * guest details in one place + quick status actions (arrived/seated/no-show/cancelled).
 * Props: { reservation, areas, onClose, onSaved }. Data comes via props (no loading state).
 */

const STATUS = {
    booked: 'הוזמן',
    arrived: 'הגיע',
    seated: 'התיישב',
    'no-show': 'לא הגיע',
    cancelled: 'בוטל',
};
const chipClass = (s) => `rd-chip rd-chip--${STATUS[s] ? s : 'booked'}`;

// "HH:MM:SS" | "HH:MM" → "HH:MM" for the read-only header.
const toHHMM = (t) => (t ? String(t).slice(0, 5) : '');

export default function ReservationDrawer({ reservation, areas = [], onClose, onSaved }) {
    // Initial (props) values — the baseline the dirty-check and the changed-only payload use.
    const initial = useMemo(() => ({
        guestName: reservation?.name ?? '',
        guestPhone: reservation?.phone ?? '',
        numberOfGuests: String(reservation?.party ?? ''),
        area: String(reservation?.area ?? 0),
        guestNotes: reservation?.guestNotes ?? '',
    }), [reservation]);

    const [form, setForm] = useState(initial);
    const [status, setStatus] = useState(reservation?.status || 'booked');
    const [saving, setSaving] = useState(false);
    const [closing, setClosing] = useState(false);   // locked after a successful save until unmount
    const [toast, setToast] = useState('');
    const inFlight = useRef(false);          // guards Save + status buttons against double-submit
    const toastTimer = useRef(null);
    const closeTimer = useRef(null);         // brief delay so the success toast is seen before we close

    // Re-seed the form when a DIFFERENT reservation opens in the same mounted drawer.
    useEffect(() => {
        setForm(initial);
        setStatus(reservation?.status || 'booked');
        setClosing(false);
        setSaving(false);
        inFlight.current = false;
    }, [initial, reservation]);

    // Close on Escape.
    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    useEffect(() => () => {
        if (toastTimer.current) clearTimeout(toastTimer.current);
        if (closeTimer.current) clearTimeout(closeTimer.current);
    }, []);

    const showToast = (msg) => {
        setToast(msg);
        if (toastTimer.current) clearTimeout(toastTimer.current);
        toastTimer.current = setTimeout(() => setToast(''), 2500);
    };

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    // Dirty = any field differs from the initial (props) value.
    const dirty = useMemo(
        () => Object.keys(initial).some((k) => form[k] !== initial[k]),
        [form, initial],
    );

    if (!reservation) return null;

    // Map a PHP failure status → Hebrew toast. Drawer stays OPEN so the user can retry.
    const failToast = (st) => {
        if (st === 418) return 'השולחן שמשובץ קטן מדי לכמות הסועדים';
        if (st === 605) return 'לא בוצע שינוי';
        if (st === 311) return 'אזור לא תקין';
        return 'השמירה נכשלה, נסו שוב';
    };

    const handleSave = async () => {
        if (!dirty || saving || inFlight.current) return;
        inFlight.current = true;
        setSaving(true);
        // Only the fields that CHANGED (the service omits undefined keys).
        const payload = { reservationId: reservation.id };
        if (form.guestName !== initial.guestName) payload.guestName = form.guestName;
        if (form.guestPhone !== initial.guestPhone) payload.guestPhone = form.guestPhone;
        if (form.numberOfGuests !== initial.numberOfGuests) payload.numberOfGuests = form.numberOfGuests;
        if (form.guestNotes !== initial.guestNotes) payload.guestNotes = form.guestNotes;
        if (form.area !== initial.area) payload.area = form.area;

        const res = await reservationsSvc.update(payload);
        if (res && res.ok) {
            window.dispatchEvent(new Event('hoppa:reservations-changed'));
            onSaved?.();
            // Show the success toast, THEN close — closing immediately would unmount the
            // drawer-local toast before the user ever sees it. We do NOT release inFlight/saving
            // here: the drawer stays open ~1100ms and clearing them would re-enable Save and allow
            // a second reservationUpdate in that window. `closing` keeps everything locked until
            // the scheduled onClose unmounts us.
            setClosing(true);
            showToast('ההזמנה עודכנה');
            if (closeTimer.current) clearTimeout(closeTimer.current);
            closeTimer.current = setTimeout(() => onClose?.(), 1100);
        } else {
            setSaving(false);
            inFlight.current = false;
            showToast(failToast(res && res.status));
        }
    };

    const handleStatus = async (next) => {
        if (saving || inFlight.current) return;
        inFlight.current = true;
        setSaving(true);
        const res = await reservationsSvc.setStatus({ reservationId: reservation.id, status: next });
        setSaving(false);
        inFlight.current = false;
        if (res && res.ok) {
            setStatus(next);
            window.dispatchEvent(new Event('hoppa:reservations-changed'));
            onSaved?.();
            showToast('הסטטוס עודכן');
        } else {
            showToast('עדכון הסטטוס נכשל, נסו שוב');
        }
    };

    // Party is required and must be ≥1 — block Save on an empty/invalid guests count (the number
    // input can be cleared, which would otherwise send Number('')===0 and get a 202 from the server).
    const partyNum = Number(form.numberOfGuests);
    const partyInvalid = form.numberOfGuests === '' || !Number.isFinite(partyNum) || partyNum < 1;
    const saveDisabled = !dirty || saving || partyInvalid || closing;

    return (
        <div className="rd-overlay" onClick={onClose} role="presentation">
            <aside
                className="rd-drawer"
                role="dialog"
                aria-modal="true"
                aria-label="עריכת הזמנה"
                dir="rtl"
                onClick={(e) => e.stopPropagation()}
            >
                {/* ── HEADER (read-only) ── */}
                <header className="rd-header">
                    <div className="rd-header-main">
                        <h2 className="rd-title">{reservation.name || '—'}</h2>
                        <span className={chipClass(status)}>{STATUS[status] || STATUS.booked}</span>
                    </div>
                    <div className="rd-header-meta" dir="ltr">
                        {reservation.date || ''}{reservation.date && reservation.time ? ' · ' : ''}{toHHMM(reservation.time)}
                    </div>
                    <button type="button" className="rd-close" onClick={onClose} aria-label="סגירה">
                        <Icon name="x" size={20} />
                    </button>
                </header>

                {/* ── QUICK STATUS ROW ── */}
                <div className="rd-status-row">
                    <button type="button" className="rd-status-btn" disabled={saving || closing} onClick={() => handleStatus('arrived')}>הגיע</button>
                    <button type="button" className="rd-status-btn" disabled={saving || closing} onClick={() => handleStatus('seated')}>התיישב</button>
                    <button type="button" className="rd-status-btn" disabled={saving || closing} onClick={() => handleStatus('no-show')}>לא הגיע</button>
                    <button type="button" className="rd-status-btn rd-status-btn--danger" disabled={saving || closing} onClick={() => handleStatus('cancelled')}>ביטול הזמנה</button>
                </div>

                {/* ── EDITABLE FORM ── */}
                <div className="rd-body">
                    <label className="rd-field">
                        <span className="rd-label">שם מלא</span>
                        <input className="rd-input" type="text" value={form.guestName} onChange={set('guestName')} placeholder="שם מלא" />
                    </label>

                    <label className="rd-field">
                        <span className="rd-label">מספר פלאפון</span>
                        <input className="rd-input" type="text" dir="ltr" value={form.guestPhone} onChange={set('guestPhone')} placeholder="050-0000000" />
                    </label>

                    <label className="rd-field">
                        <span className="rd-label">כמות סועדים</span>
                        <input className="rd-input" type="number" min="1" value={form.numberOfGuests} onChange={set('numberOfGuests')} />
                    </label>

                    <label className="rd-field">
                        <span className="rd-label">איזור</span>
                        <select className="rd-input" value={form.area} onChange={set('area')}>
                            <option value={0}>ללא אזור</option>
                            {areas.map((a) => {
                                const aid = a.ID ?? a.id;
                                return <option key={aid} value={aid}>{a.name}</option>;
                            })}
                        </select>
                    </label>

                    <label className="rd-field">
                        <span className="rd-label">הערות</span>
                        <textarea className="rd-input rd-textarea" rows={3} value={form.guestNotes} onChange={set('guestNotes')} placeholder="הערות להזמנה" />
                    </label>
                </div>

                {/* ── FOOTER ── */}
                <footer className="rd-footer">
                    <button type="button" className="rd-btn rd-btn--ghost" onClick={onClose}>ביטול</button>
                    <button type="button" className="rd-btn rd-btn--primary" disabled={saveDisabled} onClick={handleSave}>
                        {saving ? 'שומר…' : 'שמירה'}
                    </button>
                </footer>

                {toast && <div className="rd-toast" role="status">{toast}</div>}
            </aside>
        </div>
    );
}
